import type { HttpTypes } from "@medusajs/types"

const EXPIRED_CAMPAIGN_SLUGS = ["mothers-day-2026"]
const CATERING_CATEGORY_HANDLES = new Set([
  "catering",
  "catering-croissants",
  "catering-cookies",
  "catering-bakery-boxes",
  "catering-cupcakes",
  "catering-bundles",
  "catering-drinks",
  "catering-gifting",
])

export const EXPIRED_CAMPAIGN_PRODUCT_HANDLES = new Set([
  "pink-lemonade-crinkle-cookies-box-of-4-mothers-day-2026",
  "strawberry-n-cream-basque-cheesecake-mothers-day-2026",
  "watercolor-strawberry-lychee-cake-mothers-day-2026",
])

export const EXPIRED_CAMPAIGN_CATEGORY_HANDLES = new Set([
  "mothers-day-2026",
])

// Backward-compatible fallback for catering products created before backend
// category and metadata classification existed.
export const STORE_CATALOG_EXCLUDED_HANDLE_PREFIXES = ["catering-"] as const

type CatalogProduct = Pick<HttpTypes.StoreProduct, "handle"> &
  Partial<Pick<HttpTypes.StoreProduct, "categories" | "metadata">>

function getStringMetadata(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
) {
  const value = metadata?.[key]
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function productHasCategoryHandle(
  product: CatalogProduct,
  handles: Set<string>,
) {
  return (product.categories ?? []).some(
    (category) => !!category?.handle && handles.has(category.handle),
  )
}

export function isStoreCatalogExcludedHandle(handle?: string | null) {
  if (!handle) return false
  return STORE_CATALOG_EXCLUDED_HANDLE_PREFIXES.some((prefix) =>
    handle.startsWith(prefix),
  )
}

export function isHiddenCatalogProduct(product: CatalogProduct) {
  const metadata = product.metadata as Record<string, unknown> | null | undefined
  return (
    getStringMetadata(metadata, "catalog_visibility") === "hidden" ||
    isExpiredCampaignProductHandle(product.handle)
  )
}

export function isCateringCatalogProduct(product: CatalogProduct) {
  const metadata = product.metadata as Record<string, unknown> | null | undefined
  const catalogVisibility = getStringMetadata(metadata, "catalog_visibility")
  const orderType = getStringMetadata(metadata, "order_type")

  return (
    catalogVisibility === "catering" ||
    orderType === "catering" ||
    productHasCategoryHandle(product, CATERING_CATEGORY_HANDLES) ||
    isStoreCatalogExcludedHandle(product.handle)
  )
}

export function isCateringCategoryHandle(handle?: string | null) {
  if (!handle) return false
  return CATERING_CATEGORY_HANDLES.has(handle) || handle.startsWith("catering-")
}

export function isStoreCatalogProduct(product: CatalogProduct) {
  return !isHiddenCatalogProduct(product) && !isCateringCatalogProduct(product)
}

export function filterStoreCatalogProducts<
  T extends CatalogProduct,
>(products: T[]) {
  return products.filter(isStoreCatalogProduct)
}

export function isExpiredCampaignHandle(handle?: string | null) {
  if (!handle) return false

  return EXPIRED_CAMPAIGN_SLUGS.some((slug) => handle.includes(slug))
}

export function isExpiredCampaignProductHandle(handle?: string | null) {
  return (
    !!handle &&
    (EXPIRED_CAMPAIGN_PRODUCT_HANDLES.has(handle) ||
      isExpiredCampaignHandle(handle))
  )
}

export function isExpiredCampaignCategoryHandle(handle?: string | null) {
  return (
    !!handle &&
    (EXPIRED_CAMPAIGN_CATEGORY_HANDLES.has(handle) ||
      isExpiredCampaignHandle(handle))
  )
}

export function filterVisibleProducts<T extends CatalogProduct>(
  products: T[],
) {
  return products.filter((product) => !isHiddenCatalogProduct(product))
}
